"use client";

import { Fragment, type ReactNode } from "react";
import { useLayoutEffect, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Activity } from "lucide-react";
import {
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/shadcn-components/ui/table";
import { cn } from "@/lib/utils";
import { DESKTOP_HISTORY_TABLE_CLASS } from "@/components/history-view/content/historyDesktopTableLayout";

const DEFAULT_ROW_HEIGHT = 28;
const OVERSCAN = 24;
/**
 * Below this many rows the table renders EVERY row and skips virtualization entirely.
 *
 * Virtualization only pays for itself on the big history tables; the open-positions blotter is a
 * handful of rows and there a windowing miscalculation is indistinguishable from missing data — a
 * trader counting their own positions cannot tell "not rendered yet" from "closed" (SR Global,
 * 2026-09-09: a blotter headed "20 Total" showing 5 rows, while a second browser on the same account
 * showed all 20). Rendering them all removes that whole class of doubt for the sizes that matter.
 */
const VIRTUALIZE_ABOVE = 150;

export interface DesktopVirtualizedTableProps {
  rowCount: number;
  colSpan: number;
  emptyMessage: string;
  rowHeight?: number;
  tableClassName?: string;
  /** Sticky header content (`TableRow` / multiple rows). */
  header: ReactNode;
  getRowKey: (index: number) => string | number;
  renderRow: (index: number) => ReactNode;
}

function ScrollSpacerRow({
  colSpan,
  height,
}: {
  colSpan: number;
  height: number;
}) {
  if (height <= 0) return null;
  return (
    <TableRow
      aria-hidden
      className="pointer-events-none border-0 hover:bg-transparent"
    >
      <TableCell
        colSpan={colSpan}
        style={{ height, lineHeight: 0, padding: 0 }}
        className="border-0 p-0"
      />
    </TableRow>
  );
}

/**
 * Single scroll parent + native table (no shadcn Table overflow wrapper).
 * TanStack Virtual with spacer rows so column widths match thead and scroll range is correct.
 */
export function DesktopVirtualizedTable({
  rowCount,
  colSpan,
  emptyMessage,
  rowHeight = DEFAULT_ROW_HEIGHT,
  tableClassName = DESKTOP_HISTORY_TABLE_CLASS,
  header,
  getRowKey,
  renderRow,
}: DesktopVirtualizedTableProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: OVERSCAN,
    getItemKey: (index) => String(getRowKey(index)),
  });

  useLayoutEffect(() => {
    virtualizer.measure();
  }, [rowCount, rowHeight, virtualizer]);

  /**
   * Re-measure whenever the scroll box actually changes size.
   *
   * The virtualizer sizes its window from the scroll element, and this table lives in a footer panel
   * that can mount hidden or zero-height (inactive tab, collapsed panel). Measured at 0 it computes a
   * tiny window and then renders a handful of rows with full-height spacer rows around them — which on
   * screen is a few positions followed by a large blank area (SR Global, 2026-09-09). Observing the box
   * ourselves means the first real layout always re-measures, whatever order mount and reveal happen in.
   */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => virtualizer.measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [virtualizer]);

  const virtualize = rowCount > VIRTUALIZE_ABOVE;
  const items = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = virtualize && items.length > 0 ? items[0].start : 0;
  const paddingBottom =
    virtualize && items.length > 0 ? totalSize - items[items.length - 1].end : 0;
  const renderedIndexes = virtualize
    ? items.map((item) => ({ key: item.key, index: item.index }))
    : Array.from({ length: rowCount }, (_, index) => ({
        key: String(getRowKey(index)),
        index,
      }));

  return (
    <div
      ref={scrollRef}
      className="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
    >
      <table className={cn("w-full caption-bottom text-sm", tableClassName)}>
        <TableHeader className="sticky top-0 z-10 bg-background [&_tr]:border-b">
          {header}
        </TableHeader>
        {rowCount === 0 ? (
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={colSpan}
                className="py-4 text-center text-muted-foreground"
              >
                <Activity className="mx-auto mb-1.5 h-5 w-5 opacity-50" />
                <p className="text-[11px]">{emptyMessage}</p>
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody>
            <ScrollSpacerRow colSpan={colSpan} height={paddingTop} />
            {renderedIndexes.map((row) => (
              <Fragment key={row.key}>{renderRow(row.index)}</Fragment>
            ))}
            <ScrollSpacerRow colSpan={colSpan} height={paddingBottom} />
          </TableBody>
        )}
      </table>
    </div>
  );
}